/**
 * Turning a fix's age into a health band, in one place so the runner, the boat
 * greying and the on-screen readout all agree.
 *
 * A real GNSS receiver should give us a fix about once a second. If they stop
 * arriving the last position is still on the chart, which is a trap: it looks
 * healthy while the boat has actually moved on. So past a short grace we call the
 * fix "stale" (grey the boat, show its age), and past a longer gap "lost" (raise
 * a plain warning). The thresholds are deliberately gentle so a single dropped
 * second doesn't flicker the boat.
 *
 * This is for the real device feed only. The simulator drives a continuous
 * position and is never treated as stale.
 */
export const STALE_AFTER_MS = 10_000; // ~10 s without a fresh fix -> stale
export const LOST_AFTER_MS = 30_000; // ~30 s -> lost, warn plainly

export type FixHealth = 'live' | 'stale' | 'lost';

/** Band a fix age (ms) into live / stale / lost. */
export function fixHealthForAge(ageMs: number): FixHealth {
  if (ageMs >= LOST_AFTER_MS) return 'lost';
  if (ageMs >= STALE_AFTER_MS) return 'stale';
  return 'live';
}

/** A short human age, e.g. "42s ago" or "2m 05s ago". */
export function formatFixAge(ageMs: number): string {
  const sec = Math.max(0, Math.round(ageMs / 1000));
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${String(s).padStart(2, '0')}s ago`;
}
